import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { EventLifecycleStatus } from '../../types/events.types';
import { useIdempotentAppMutation } from '../use-idempotent-app-mutation.hook';
import { pendingEventsStore, PendingMutationSettleOutcome } from './pending-events.store';
import { useOptimisticEvents } from './use-optimistic-events';

type DeleteEventVars = {
  companyId: string;
  eventId: string;
  // Fires once when the queued EVENT_MUTATION apply settles (list poll observes it), not when
  // the DELETE resolves — success/failure toasts belong here so they match the row state.
  onApplySettled?: (outcome: PendingMutationSettleOutcome) => void;
};
type OptimisticRollback = { rollback: () => void };

export const useDelete = () => {
  const optimistic = useOptimisticEvents();

  const { mutate: deleteEvent, isPending } = useIdempotentAppMutation<
    unknown,
    HttpException,
    DeleteEventVars,
    OptimisticRollback
  >([QueryKey.DELETE_EVENT], {
    defaultErrorHandling: false,
    mutationFn: ({ companyId, eventId }, { headers }) =>
      ApiService.delete(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'events', eventId],
        },
        { headers },
      ),
    // Optimistically swap the row for the create-style skeleton (pending-delete); polling drops
    // it once the queue applies.
    // The store entry keeps useEvents polling fast and carries `onApplySettled` so the success
    // toast fires when the delete actually lands, not when the DELETE request resolves.
    onMutate: async ({ companyId, eventId, onApplySettled }) => {
      await optimistic.cancel();
      const previous = optimistic.snapshot();
      optimistic.setStatus(eventId, EventLifecycleStatus.PENDING_DELETE);
      pendingEventsStore
        .getState()
        .addPendingDelete({ companyId, eventId, onApplySettled, requestedAtMs: Date.now() });
      return { rollback: () => optimistic.restore(previous) };
    },
    // Settle detection only trusts fetches newer than this acknowledgement (see useEvents).
    onSuccess: (_data, { eventId }) => {
      pendingEventsStore.getState().markPendingDeleteEnqueued(eventId);
    },
    onError: (_error, variables, context) => {
      pendingEventsStore.getState().removePendingDelete(variables.eventId);
      context?.rollback();
    },
  });

  return { deleteEvent, isPending };
};
